import { useEffect, useState } from 'react';
import { useDropzone, type Accept } from 'react-dropzone';
import { cn } from '@/lib/utils';

const MAX_FILE_BYTES = 5 * 1024 * 1024;

/**
 * One dropzone for both images and PDFs. The API still distinguishes `image`
 * from `pdf`, but that is derived from the picked file's MIME type, so the user
 * never has to declare it up front. Keep this in sync with the server's
 * ALLOWED_IMAGE_TYPES and ALLOWED_PDF_TYPES.
 */
const ACCEPT: Accept = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/pdf': ['.pdf'],
};

const PROMPT = 'Drop a PNG, JPEG, or PDF (or click to select)';

/** Derives the API's input_type from the file itself. */
export function fileInputType(file: File): 'image' | 'pdf' {
  return file.type === 'application/pdf' ? 'pdf' : 'image';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export interface FileDropzoneProps {
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
}

export function FileDropzone({ file, onChange, disabled }: FileDropzoneProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Image previews via createObjectURL — must revoke on file change/unmount
  // to avoid leaking blob: URLs.
  useEffect(() => {
    if (!file || fileInputType(file) !== 'image') {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept: ACCEPT,
    maxSize: MAX_FILE_BYTES,
    multiple: false,
    disabled,
    onDrop: (accepted) => {
      const next = accepted[0];
      if (next) onChange(next);
    },
  });

  const rejection = fileRejections[0]?.errors[0];
  const rejectionMessage =
    rejection?.code === 'file-too-large'
      ? `File too large. Max ${formatBytes(MAX_FILE_BYTES)}.`
      : rejection?.code === 'file-invalid-type'
        ? 'Unsupported file type. Use PNG, JPEG, or PDF.'
        : rejection?.message ?? null;

  return (
    <div>
      <div
        {...getRootProps()}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-8 text-center transition-colors',
          isDragActive
            ? 'border-primary bg-primary/5'
            : 'border-border bg-card hover:border-primary/50',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <input {...getInputProps()} />
        {file ? (
          <FileSummary file={file} previewUrl={previewUrl} />
        ) : (
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground">{PROMPT}</p>
            <p className="mt-2 text-xs">Max {formatBytes(MAX_FILE_BYTES)}</p>
          </div>
        )}
      </div>

      {file && !disabled && (
        <div className="mt-2 text-right">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            className="text-xs text-muted-foreground underline hover:text-foreground"
          >
            Remove
          </button>
        </div>
      )}

      {rejectionMessage && (
        <p className="mt-2 text-xs text-destructive">{rejectionMessage}</p>
      )}
    </div>
  );
}

function FileSummary({
  file,
  previewUrl,
}: {
  file: File;
  previewUrl: string | null;
}) {
  const kind = fileInputType(file);
  return (
    <div className="flex items-center gap-4">
      {kind === 'image' && previewUrl ? (
        <img
          src={previewUrl}
          alt="preview"
          className="max-h-32 max-w-32 rounded border border-border object-contain"
        />
      ) : (
        <div className="flex h-16 w-16 items-center justify-center rounded border border-border bg-muted text-2xl">
          {kind === 'pdf' ? '📄' : '🖼️'}
        </div>
      )}
      <div className="text-left">
        <p className="font-medium text-foreground line-clamp-1">{file.name}</p>
        <p className="mt-1 text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>
    </div>
  );
}
